import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { updateLastActivity } from '@/utils/sessionStorage'

// Define types directly to avoid Database type issues
type AccountContact = {
  id: string
  account_id: string
  jid: string
  is_group: boolean
  display_name: string | null
  push_name_last: string | null
  email: string | null
  phone: string | null
  account_assistant_id: string | null
  assistant_is_active: boolean | null
  last_message_at: string | null
  last_message_id: string | null
  created_at: string | null
  updated_at: string | null
}

type AccountMessage = {
  id: number
  account_id: string
  remote_jid: string
  from_jid: string
  from_me: boolean
  wa_message_id: string
  msg_type: string
  text_content: string | null
  status: string | null
  sent_at: string
  received_at: string | null
  media_kind: string | null
  media_url: string | null
  raw_payload: any
}

type AccountAssistant = {
  id: string
  account_id: string
  name: string
  settings: string | null
  model: string
  response_time_value: number
  response_time_unit: string
  max_messages: number
  prompt_mode: string
  default_prompt: string | null
  voice_tone: string | null
  main_objective: string | null
  language_rules: string | null
  good_examples: string | null
  restrictions: string | null
  additional_info: string | null
  is_active: boolean
  created_at: string | null
  updated_at: string | null
}

type AccountCampaign = {
  id: string
  account_id: string
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tag_ids: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

interface DashboardStats {
  totalContacts: number
  totalMessages: number
  activeAssistants: number
  activeCampaigns: number
  todayMessages: number
  weekMessages: number
  monthMessages: number
}

interface MessagesByDay {
  date: string
  messages: number
  sent: number
  received: number
}

interface MessagesByType {
  type: string
  count: number
  percentage: number
}

interface TopAssistant {
  id: string
  name: string
  messageCount: number
  contactCount: number
  isActive: boolean
}

interface TopCampaign {
  id: string
  name: string
  contactCount: number
  isActive: boolean
  triggerType: string
}

interface UseDashboardReturn {
  stats: DashboardStats
  messagesByDay: MessagesByDay[]
  messagesByType: MessagesByType[]
  topAssistants: TopAssistant[]
  topCampaigns: TopCampaign[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useDashboard = (): UseDashboardReturn => {
  const { user } = useAuth()
  const [stats, setStats] = useState<DashboardStats>({
    totalContacts: 0,
    totalMessages: 0,
    activeAssistants: 0,
    activeCampaigns: 0,
    todayMessages: 0,
    weekMessages: 0,
    monthMessages: 0
  })
  const [messagesByDay, setMessagesByDay] = useState<MessagesByDay[]>([])
  const [messagesByType, setMessagesByType] = useState<MessagesByType[]>([])
  const [topAssistants, setTopAssistants] = useState<TopAssistant[]>([])
  const [topCampaigns, setTopCampaigns] = useState<TopCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDashboardData = async (): Promise<void> => {
    try {
      setLoading(true)
      setError(null)

      console.log('📊 [useDashboard] Iniciando busca de dados do dashboard...')

      if (!user) {
        console.log('❌ [useDashboard] Usuário não está logado')
        setError('Usuário não está logado')
        setLoading(false)
        return
      }

      if (!user.account_id) {
        console.log('❌ [useDashboard] account_id não encontrado no usuário')
        setError('account_id não encontrado')
        setLoading(false)
        return
      }

      if (!supabase) {
        console.log('❌ [useDashboard] Supabase não está configurado')
        setError('Supabase não está configurado')
        setLoading(false)
        return
      }

      // Buscar estatísticas básicas
      console.log('📊 [useDashboard] Buscando estatísticas básicas...')
      
      const [
        contactsResult,
        messagesResult,
        assistantsResult,
        campaignsResult
      ] = await Promise.all([
        // Total de contatos
        supabase
          .from('account_contacts')
          .select('id', { count: 'exact' })
          .eq('account_id', user.account_id),
        
        // Total de mensagens
        supabase
          .from('account_messages')
          .select('id, sent_at, from_me', { count: 'exact' })
          .eq('account_id', user.account_id),
        
        // Assistentes ativos
        supabase
          .from('account_assistants')
          .select('id', { count: 'exact' })
          .eq('account_id', user.account_id)
          .eq('is_active', true),
        
        // Campanhas ativas
        supabase
          .from('account_campaigns')
          .select('id', { count: 'exact' })
          .eq('account_id', user.account_id)
          .eq('is_active', true)
      ])

      // Calcular estatísticas de tempo
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

      let todayMessages = 0
      let weekMessages = 0
      let monthMessages = 0

      if (messagesResult.data) {
        messagesResult.data.forEach(message => {
          const messageDate = new Date(message.sent_at)
          if (messageDate >= today) todayMessages++
          if (messageDate >= weekAgo) weekMessages++
          if (messageDate >= monthAgo) monthMessages++
        })
      }

      const dashboardStats: DashboardStats = {
        totalContacts: contactsResult.count || 0,
        totalMessages: messagesResult.count || 0,
        activeAssistants: assistantsResult.count || 0,
        activeCampaigns: campaignsResult.count || 0,
        todayMessages,
        weekMessages,
        monthMessages
      }

      setStats(dashboardStats)

      // Buscar mensagens por dia (últimos 7 dias)
      console.log('📊 [useDashboard] Buscando mensagens por dia...')
      
      const { data: messagesData } = await supabase
        .from('account_messages')
        .select('sent_at, from_me')
        .eq('account_id', user.account_id)
        .gte('sent_at', weekAgo.toISOString())
        .order('sent_at', { ascending: true })

      // Agrupar mensagens por dia
      const messagesByDayMap = new Map<string, { messages: number; sent: number; received: number }>()
      
      // Inicializar últimos 7 dias
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000)
        const dateStr = date.toISOString().split('T')[0]
        messagesByDayMap.set(dateStr, { messages: 0, sent: 0, received: 0 })
      }

      messagesData?.forEach(message => {
        const dateStr = message.sent_at.split('T')[0]
        const dayData = messagesByDayMap.get(dateStr)
        if (dayData) {
          dayData.messages++
          if (message.from_me) {
            dayData.sent++
          } else {
            dayData.received++
          }
        }
      })

      const messagesByDayArray: MessagesByDay[] = Array.from(messagesByDayMap.entries()).map(([date, data]) => ({
        date,
        messages: data.messages,
        sent: data.sent,
        received: data.received
      }))

      setMessagesByDay(messagesByDayArray)

      // Buscar mensagens por tipo
      console.log('📊 [useDashboard] Buscando mensagens por tipo...')
      
      const { data: messageTypesData } = await supabase
        .from('account_messages')
        .select('msg_type')
        .eq('account_id', user.account_id)

      const typeCountMap = new Map<string, number>()
      messageTypesData?.forEach(message => {
        const count = typeCountMap.get(message.msg_type) || 0
        typeCountMap.set(message.msg_type, count + 1)
      })

      const totalTypeMessages = messageTypesData?.length || 1
      const messagesByTypeArray: MessagesByType[] = Array.from(typeCountMap.entries()).map(([type, count]) => ({
        type,
        count,
        percentage: Math.round((count / totalTypeMessages) * 100)
      }))

      setMessagesByType(messagesByTypeArray)

      // Buscar top assistentes
      console.log('📊 [useDashboard] Buscando top assistentes...')
      
      const { data: assistantsData } = await supabase
        .from('account_assistants')
        .select(`
          id,
          name,
          is_active,
          contacts:account_contacts(id)
        `)
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })
        .limit(10)

      const topAssistantsArray: TopAssistant[] = (assistantsData || []).map(assistant => ({
        id: assistant.id,
        name: assistant.name,
        messageCount: 0, // Seria necessário uma query mais complexa para contar mensagens
        contactCount: (assistant as any).contacts?.length || 0,
        isActive: assistant.is_active
      }))

      setTopAssistants(topAssistantsArray)

      // Buscar top campanhas
      console.log('📊 [useDashboard] Buscando top campanhas...')
      
      const { data: campaignsData } = await supabase
        .from('account_campaigns')
        .select(`
          id,
          name,
          is_active,
          trigger_type,
          contacts:account_contacts(id)
        `)
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })
        .limit(10)

      const topCampaignsArray: TopCampaign[] = (campaignsData || []).map(campaign => ({
        id: campaign.id,
        name: campaign.name,
        contactCount: (campaign as any).contacts?.length || 0,
        isActive: campaign.is_active || false,
        triggerType: campaign.trigger_type
      }))

      setTopCampaigns(topCampaignsArray)

      console.log('✅ [useDashboard] Dados do dashboard carregados com sucesso:', {
        stats: dashboardStats,
        messagesByDay: messagesByDayArray.length,
        messagesByType: messagesByTypeArray.length,
        topAssistants: topAssistantsArray.length,
        topCampaigns: topCampaignsArray.length
      })

    } catch (err) {
      console.error('💥 [useDashboard] Erro inesperado:', err)
      setError(`Erro inesperado: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
    } finally {
      setLoading(false)
    }
  }

  const refetch = async (): Promise<void> => {
    await fetchDashboardData()
  }

  useEffect(() => {
    if (user) {
      updateLastActivity()
      fetchDashboardData()
    }
  }, [user])

  return {
    stats,
    messagesByDay,
    messagesByType,
    topAssistants,
    topCampaigns,
    loading,
    error,
    refetch,
  }
}